import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { plainError } from '../../lib/sandbox';
import { Eye, EyeOff, X, ChevronRight, LogIn } from 'lucide-react';

const ACCOUNTS_KEY = '3wi.saved-accounts';

interface SavedAccount {
  email: string;
  fullName: string;
  avatarColor: string;
  lastAccess: string;
}

const AVATAR_COLORS = ['#0d9488', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#059669', '#4f46e5', '#0891b2'];

function getAvatarColor(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) hash = ((hash << 5) - hash + email.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initials(name: string): string {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function loadAccounts(): SavedAccount[] {
  try {
    return JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || '[]');
  } catch { return []; }
}

function saveAccount(email: string, fullName: string) {
  const accounts = loadAccounts().filter(a => a.email !== email);
  accounts.unshift({ email, fullName, avatarColor: getAvatarColor(email), lastAccess: new Date().toISOString() });
  if (accounts.length > 5) accounts.pop();
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

function removeAccount(email: string) {
  const accounts = loadAccounts().filter(a => a.email !== email);
  localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(accounts));
}

export function Login() {
  const [step, setStep] = useState<'picker' | 'password'>('picker');
  const [accounts, setAccounts] = useState<SavedAccount[]>([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const setSession = useAuthStore(s => s.setSession);
  const nav = useNavigate();

  useEffect(() => {
    const saved = loadAccounts();
    setAccounts(saved);
    if (saved.length === 0) setStep('password');
  }, []);

  function selectAccount(account: SavedAccount) {
    setEmail(account.email);
    setError(null);
    setStep('password');
  }

  function handleRemove(e: React.MouseEvent, email: string) {
    e.stopPropagation();
    removeAccount(email);
    const updated = loadAccounts();
    setAccounts(updated);
    if (updated.length === 0) setStep('password');
  }

  function useDifferent() {
    setEmail('');
    setPassword('');
    setError(null);
    setStep('password');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data, error: authErr } = await supabase.auth.signInWithPassword({ email, password });
      if (authErr) throw new Error(authErr.message);
      if (!data.session) throw new Error('No session returned');

      const { data: userRows, error: dbErr } = await supabase
        .from('rainmaker_users')
        .select('id, full_name, email, status')
        .eq('auth_user_id', data.user.id)
        .limit(1);
      if (dbErr) throw new Error(dbErr.message);

      const appUser = userRows?.[0];
      const fullName = appUser?.full_name ?? data.user.email ?? 'User';
      const role = appUser?.status === 'active' ? 'director' : 'staff';

      saveAccount(email, fullName);

      setSession({
        user: {
          id: appUser?.id ?? data.user.id,
          tenantId: '',
          role,
          fullName,
          email: data.user.email,
        },
        accessToken: data.session.access_token,
        refreshToken: data.session.refresh_token,
      });
      // Land on role-derived home: operators → operator dashboard, managers/directors → finance.
      nav('/home');
    } catch (e) {
      // This printed Chrome's "Failed to fetch" to a salesperson with no signal (Ravi, 00:40). Signing
      // in genuinely CANNOT work offline — it is a round trip to the auth server, and no cache can
      // stand in for it — so the honest thing is to say that, not to name a function that failed.
      setError(plainError(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1e293b 100%)' }}>
      <div className="w-full max-w-[400px]">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ background: 'linear-gradient(135deg, #2dd4bf, #059669)' }}>
            <span className="text-white font-bold text-lg">R</span>
          </div>
          <h1 className="text-[22px] font-bold text-white">Rainmaker</h1>
          <p className="text-[13px] text-[#64748b] mt-1">Sign in to your account</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {step === 'picker' && accounts.length > 0 ? (
            <div>
              <div className="px-6 pt-6 pb-3">
                <h2 className="text-[15px] font-semibold text-[#0f172a]">Choose an account</h2>
                <p className="text-[12px] text-[#64748b] mt-0.5">to continue to Rainmaker</p>
              </div>

              <div className="px-3 pb-2">
                {accounts.map(account => (
                  <button
                    key={account.email}
                    onClick={() => selectAccount(account)}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-[#f8fafc] transition-colors group text-left"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-semibold flex-shrink-0"
                      style={{ backgroundColor: account.avatarColor }}
                    >
                      {initials(account.fullName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[#0f172a] truncate">{account.fullName}</p>
                      <p className="text-[11px] text-[#64748b] truncate">{account.email}</p>
                      <p className="text-[10px] text-[#94a3b8] truncate">Last accessed {new Date(account.lastAccess).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                    </div>
                    <button
                      onClick={(e) => handleRemove(e, account.email)}
                      className="p-1 rounded-full opacity-0 group-hover:opacity-100 hover:bg-[#fee2e2] text-[#94a3b8] hover:text-[#dc2626] transition-all"
                      title="Remove account"
                    >
                      <X size={14} />
                    </button>
                    <ChevronRight size={16} className="text-[#cbd5e1] group-hover:text-[#94a3b8] transition-colors" />
                  </button>
                ))}
              </div>

              <div className="border-t border-[#f1f5f9] px-6 py-4">
                <button
                  onClick={useDifferent}
                  className="w-full flex items-center justify-center gap-2 text-[13px] font-medium text-[#0d9488] hover:text-[#0f766e] transition-colors"
                >
                  <LogIn size={14} />
                  Use a different account
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="px-6 py-6 space-y-4">
              {accounts.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setStep('picker'); setError(null); }}
                  className="text-[12px] text-[#0d9488] hover:underline mb-1"
                >
                  &larr; Back to accounts
                </button>
              )}

              <div>
                <label htmlFor="login-email" className="block text-[12px] font-medium text-[#334155] mb-1.5">Email address</label>
                <input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus={!email}
                  autoComplete="email"
                  placeholder="you@company.com"
                  className="w-full px-3 py-2.5 border border-[#e2e8f0] rounded-lg text-[13px] text-[#0f172a] placeholder-[#cbd5e1] focus:outline-none focus:ring-2 focus:ring-[#2dd4bf] focus:border-transparent transition-shadow"
                />
              </div>

              <div>
                <label htmlFor="login-password" className="block text-[12px] font-medium text-[#334155] mb-1.5">Password</label>
                <div className="relative">
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    autoFocus={!!email}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    className="w-full px-3 py-2.5 pr-10 border border-[#e2e8f0] rounded-lg text-[13px] text-[#0f172a] placeholder-[#cbd5e1] focus:outline-none focus:ring-2 focus:ring-[#2dd4bf] focus:border-transparent transition-shadow"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b] transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-[12px] text-[#dc2626] bg-[#fef2f2] border border-[#fecaca] rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg text-[13px] font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: loading ? '#94a3b8' : 'linear-gradient(135deg, #2dd4bf, #059669)' }}
              >
                {loading ? 'Signing in…' : 'Sign in'}
              </button>

              <div className="flex items-center justify-between pt-1">
                <Link to="/auth/forgot" className="text-[12px] text-[#64748b] hover:text-[#0d9488] transition-colors">
                  Forgot password?
                </Link>
                {/* "Create account" hidden for the demo — self-service signup is a
                    parked Express path (Signup.tsx → Express /auth/signup, dead on
                    deployed staging). Lane A ports it to Supabase Auth post-demo;
                    restore this Link then. */}
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-[10px] text-[#475569] mt-6">
          By signing in, you agree to our Terms of Service and Privacy Policy.
          <br />
          Data hosted in EU · POPIA compliant.
        </p>
      </div>
    </div>
  );
}
